import React from "react";
import { Box, Chip, Grid, LinearProgress, Typography } from "@mui/material";
import { cardColors, cardStyle } from "../../styles/cardStyles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

/**
 * Component for displaying a summary bar chart of all predictions
 * @param {Object} props - Component props
 * @param {Object} props.scores - Object containing all prediction scores
 * @return {JSX.Element} - Rendered summary chart component
 */
const PredictionSummary = ({ scores }) => {
  const getClassification = (score, thresholds, invertColors = false) => {
    if (score === null || score === undefined) return "unknown";

    if (invertColors) {
      // SIFT: lower is more deleterious
      if (score <= 0.05) return "pathogenic";
      return "benign";
    } else {
      // Standard: higher is more pathogenic
      if (thresholds.length === 1) {
        return score >= thresholds[0] ? "pathogenic" : "benign";
      } else if (thresholds.length === 2) {
        if (score < thresholds[0]) return "benign";
        if (score >= thresholds[1]) return "pathogenic";
        return "uncertain";
      }
    }
    return "unknown";
  };

  const predictions = [
    {
      name: "SIFT",
      value: scores.sift,
      classification: getClassification(scores.sift, [0.05], true),
      thresholds: [0.05],
      invertColors: true,
    },
    {
      name: "PolyPhen",
      value: scores.polyphen,
      classification: getClassification(scores.polyphen, [0.446, 0.908]),
      thresholds: [0.446, 0.908],
    },
    {
      name: "AlphaMissense",
      value: scores.alphaMissense,
      classification: getClassification(scores.alphaMissense, [0.34, 0.564]),
      thresholds: [0.34, 0.564],
    },
    {
      name: "REVEL",
      value: scores.revel,
      classification: getClassification(scores.revel, [0.5, 0.75]),
      thresholds: [0.5, 0.75],
    },
    {
      name: "CADD",
      value: scores.cadd,
      classification: getClassification(scores.cadd, [20, 30]),
      thresholds: [20, 30],
      maxValue: 50,
    },
    {
      name: "BayesDel",
      value: scores.bayesDel,
      classification: getClassification(scores.bayesDel, [-0.36, 0.28]),
      thresholds: [-0.36, 0.28],
      maxValue: 1,
    },
    {
      name: "MetaRNN",
      value: scores.metaRNN,
      classification: getClassification(scores.metaRNN, [0.5]),
      thresholds: [0.5],
    },
    {
      name: "PrimateAI",
      value: scores.primateAI,
      classification: getClassification(scores.primateAI, [0.8]),
      thresholds: [0.8],
    },
  ].filter((p) => p.value !== null && p.value !== undefined);

  const pathogenicCount = predictions.filter(
    (p) => p.classification === "pathogenic"
  ).length;
  const benignCount = predictions.filter(
    (p) => p.classification === "benign"
  ).length;
  const uncertainCount = predictions.filter(
    (p) => p.classification === "uncertain"
  ).length;

  return (
    <Box sx={{ mb: 2, p: 2, bgcolor: "#f8f9fa", borderRadius: 2 }}>
      <Typography
        variant="subtitle2"
        color="#333333"
        fontWeight="bold"
        sx={{ mb: 1.5 }}
      >
        Prediction Summary ({predictions.length} tools)
      </Typography>

      <Grid container spacing={2} alignItems="center">
        <Grid item xs={8}>
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
            {predictions.map((pred, idx) => {
              const colors = {
                pathogenic: "#ef5350",
                benign: "#66bb6a",
                uncertain: "#ffc107",
                unknown: "#9e9e9e",
              };

              return (
                <Box
                  key={idx}
                  sx={{
                    flex: "1 1 auto",
                    minWidth: "60px",
                    height: 50,
                    bgcolor: colors[pred.classification],
                    borderRadius: 1,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    transition: "transform 0.2s",
                    "&:hover": {
                      transform: "scale(1.05)",
                      zIndex: 1,
                      boxShadow: 2,
                    },
                  }}
                >
                  <Typography
                    variant="caption"
                    color="white"
                    fontWeight="bold"
                    sx={{ fontSize: "0.65rem" }}
                  >
                    {pred.name}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="white"
                    sx={{ fontSize: "0.6rem" }}
                  >
                    {pred.value?.toFixed(2)}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Grid>

        <Grid item xs={4}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <CancelIcon sx={{ fontSize: 14, color: "#ef5350" }} />
              <Typography variant="caption" color="#666">
                Pathogenic: {pathogenicCount}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <HelpOutlineIcon sx={{ fontSize: 14, color: "#ffc107" }} />
              <Typography variant="caption" color="#666">
                Uncertain: {uncertainCount}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <CheckCircleIcon sx={{ fontSize: 14, color: "#66bb6a" }} />
              <Typography variant="caption" color="#666">
                Benign: {benignCount}
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

/**
 * Component for displaying a prediction score with a visual slider and thresholds
 * @param {Object} props - Component props
 * @param {string} props.label - Label for the prediction score
 * @param {number|string} props.value - Score value
 * @param {number} [props.maxValue=1] - Maximum value for the score
 * @param {string} [props.interpretation] - Interpretation text
 * @param {Array<Object>} [props.thresholds] - Array of threshold objects with value and label
 * @param {boolean} [props.invertColors=false] - If true, lower values are more severe (e.g., SIFT)
 * @param {boolean} [props.parseValue=false] - If true, parse value from format like "tolerated(0.14)"
 * @return {JSX.Element} - Rendered prediction slider component
 */
const PredictionSlider = ({
  label,
  value,
  maxValue = 1,
  interpretation,
  thresholds = [],
  invertColors = false,
  parseValue = false,
}) => {
  // Parse value if it's in format like "tolerated(0.14)" or "deleterious(0.02)"
  const getParsedValue = () => {
    if (!value) return 0;

    if (parseValue && typeof value === "string") {
      const match = value.match(/\(([0-9.-]+)\)/);
      if (match && match[1]) {
        return parseFloat(match[1]);
      }
    }

    return parseFloat(value) || 0;
  };

  const numValue = getParsedValue();
  const percentage = Math.min(Math.max((numValue / maxValue) * 100, 0), 100);

  // Color based on thresholds and severity
  const getColor = () => {
    if (thresholds.length === 0) {
      if (invertColors) {
        if (percentage <= 30) return "#ef5350";
        if (percentage <= 60) return "#ff9800";
        return "#66bb6a";
      } else {
        if (percentage >= 70) return "#ef5350";
        if (percentage >= 40) return "#ff9800";
        return "#66bb6a";
      }
    }

    const sortedThresholds = [...thresholds].sort((a, b) => a.value - b.value);

    if (invertColors) {
      if (numValue <= sortedThresholds[0].value) {
        return "#ef5350";
      }
      if (
        sortedThresholds.length > 1 &&
        numValue <= sortedThresholds[1].value
      ) {
        return "#ffc107";
      }
      return "#66bb6a";
    } else {
      if (numValue >= sortedThresholds[sortedThresholds.length - 1].value) {
        return "#ef5350";
      }
      if (
        sortedThresholds.length > 1 &&
        numValue >= sortedThresholds[sortedThresholds.length - 2].value
      ) {
        return "#ff9800";
      }
      if (numValue >= sortedThresholds[0].value) {
        return "#ffc107";
      }
      return "#66bb6a";
    }
  };

  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.3 }}>
        <Typography
          variant="caption"
          color="#555555"
          fontWeight="medium"
          sx={{ fontSize: "0.75rem" }}
        >
          {label}
        </Typography>
        <Typography
          variant="caption"
          color="#111111"
          fontWeight="bold"
          sx={{ fontSize: "0.75rem" }}
        >
          {numValue.toFixed(3)}
        </Typography>
      </Box>

      <Box sx={{ position: "relative" }}>
        <LinearProgress
          variant="determinate"
          value={percentage}
          sx={{
            height: 6,
            borderRadius: 3,
            bgcolor: "#e0e0e0",
            "& .MuiLinearProgress-bar": {
              bgcolor: getColor(),
              borderRadius: 3,
            },
          }}
        />

        {/* Threshold markers */}
        {thresholds.map((threshold, index) => {
          const thresholdPercentage = Math.min(
            Math.max((threshold.value / maxValue) * 100, 0),
            100
          );
          return (
            <Box
              key={index}
              sx={{
                position: "absolute",
                left: `${thresholdPercentage}%`,
                top: -1,
                bottom: -1,
                width: 2,
                bgcolor: "#333333",
                zIndex: 1,
                opacity: 0.6,
              }}
            />
          );
        })}
      </Box>

      {/* Compact threshold labels */}
      {thresholds.length > 0 && (
        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.3 }}>
          {thresholds.map((threshold, index) => (
            <Typography
              key={index}
              variant="caption"
              color="#888888"
              sx={{ fontSize: "0.6rem" }}
            >
              {threshold.label}: {threshold.value}
            </Typography>
          ))}
        </Box>
      )}

      {interpretation && (
        <Typography
          variant="caption"
          color="#666666"
          sx={{
            mt: 0.3,
            display: "block",
            fontStyle: "italic",
            fontSize: "0.65rem",
          }}
        >
          {interpretation}
        </Typography>
      )}
    </Box>
  );
};

/**
 * Component for displaying SpliceAI scores in a compact grid
 * @param {Object} props - Component props
 * @param {Object} props.variant - Variant data object containing SpliceAI scores
 * @return {JSX.Element} - Rendered SpliceAI section component
 */
const SpliceAISection = ({ variant }) => {
  const spliceScores = [
    {
      label: "AG",
      fullLabel: "Acceptor Gain",
      dp: variant.spliceai_pred_dp_ag,
      ds: variant.spliceai_pred_ds_ag,
    },
    {
      label: "AL",
      fullLabel: "Acceptor Loss",
      dp: variant.spliceai_pred_dp_al,
      ds: variant.spliceai_pred_ds_al,
    },
    {
      label: "DG",
      fullLabel: "Donor Gain",
      dp: variant.spliceai_pred_dp_dg,
      ds: variant.spliceai_pred_ds_dg,
    },
    {
      label: "DL",
      fullLabel: "Donor Loss",
      dp: variant.spliceai_pred_dp_dl,
      ds: variant.spliceai_pred_ds_dl,
    },
  ];

  const maxScore = parseFloat(variant.spliceai_max_score) || 0;

  return (
    <Box sx={{ mt: 2, p: 1.5, bgcolor: "#f8f9fa", borderRadius: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Typography
          variant="caption"
          color="#333333"
          fontWeight="bold"
          sx={{ fontSize: "0.8rem" }}
        >
          SpliceAI Predictions
        </Typography>
        {variant.spliceai_interpretation && (
          <Chip
            label={variant.spliceai_interpretation}
            size="small"
            sx={{
              bgcolor:
                maxScore > 0.5
                  ? "#ffebee"
                  : maxScore > 0.2
                  ? "#fff3e0"
                  : "#e8f5e9",
              color:
                maxScore > 0.5
                  ? "#c62828"
                  : maxScore > 0.2
                  ? "#e65100"
                  : "#2e7d32",
              fontWeight: "bold",
              fontSize: "0.65rem",
              height: 20,
            }}
          />
        )}
      </Box>

      <Grid container spacing={1} sx={{ mb: 1 }}>
        {spliceScores.map((score, index) => {
          const dsValue = parseFloat(score.ds || 0);
          const dpValue = parseInt(score.dp || 0);
          return (
            <Grid item xs={6} sm={3} key={index}>
              <Box
                sx={{
                  bgcolor: "white",
                  p: 1,
                  borderRadius: 1,
                  boxShadow: 1,
                  height: "100%",
                }}
              >
                <Typography
                  variant="caption"
                  color="#333333"
                  sx={{
                    display: "block",
                    mb: 0.5,
                    fontWeight: "bold",
                    fontSize: "0.7rem",
                  }}
                  title={score.fullLabel}
                >
                  {score.label}
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 0.3,
                  }}
                >
                  <Typography
                    variant="caption"
                    color="#1976d2"
                    sx={{ fontSize: "0.65rem" }}
                  >
                    DS:
                  </Typography>
                  <Typography
                    variant="caption"
                    fontWeight="bold"
                    color="#1565c0"
                    sx={{ fontSize: "0.65rem" }}
                  >
                    {dsValue.toFixed(2)}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 0.5,
                  }}
                >
                  <Typography
                    variant="caption"
                    color="#7b1fa2"
                    sx={{ fontSize: "0.65rem" }}
                  >
                    DP:
                  </Typography>
                  <Typography
                    variant="caption"
                    fontWeight="bold"
                    color="#6a1b9a"
                    sx={{ fontSize: "0.65rem" }}
                  >
                    {dpValue > 0 ? `+${dpValue}` : dpValue}
                  </Typography>
                </Box>

                <LinearProgress
                  variant="determinate"
                  value={Math.min(dsValue * 100, 100)}
                  sx={{
                    height: 4,
                    borderRadius: 2,
                    bgcolor: "#e0e0e0",
                    "& .MuiLinearProgress-bar": {
                      bgcolor:
                        dsValue > 0.5
                          ? "#ef5350"
                          : dsValue > 0.2
                          ? "#ff9800"
                          : "#66bb6a",
                      borderRadius: 2,
                    },
                  }}
                />
              </Box>
            </Grid>
          );
        })}
      </Grid>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 1,
          mt: 2,
          bgcolor: "white",
          borderRadius: 1,
        }}
      >
        <Typography
          variant="caption"
          color="#555555"
          fontWeight="medium"
          sx={{ fontSize: "0.7rem" }}
        >
          Max Score:
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            variant="caption"
            color="#111111"
            fontWeight="bold"
            sx={{ fontSize: "0.7rem" }}
          >
            {maxScore.toFixed(3)}
          </Typography>
          <Chip
            label={
              maxScore > 0.5 ? "High" : maxScore > 0.2 ? "Moderate" : "Low"
            }
            size="small"
            sx={{
              bgcolor:
                maxScore > 0.5
                  ? "#ffebee"
                  : maxScore > 0.2
                  ? "#fff3e0"
                  : "#e8f5e9",
              color:
                maxScore > 0.5
                  ? "#c62828"
                  : maxScore > 0.2
                  ? "#e65100"
                  : "#2e7d32",
              fontWeight: "bold",
              fontSize: "0.6rem",
              height: 18,
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

/**
 * Component for displaying in silico prediction information
 * @param {Object} props - Component props
 * @param {Object} props.variant - Variant data object
 * @return {JSX.Element} - Rendered component
 */
function InSilicoCard({ variant }) {
  if (!variant) return null;

  // Extract interpretation from SIFT score field (e.g., "tolerated(0.14)")
  const getSiftInterpretation = () => {
    if (!variant.sift_score) return null;
    const match = variant.sift_score.match(/^([a-z_]+)\(/i);
    return match ? match[1].replace(/_/g, " ") : null;
  };

  // Extract interpretation from PolyPhen score field (e.g., "benign(0.123)")
  const getPolyphenInterpretation = () => {
    if (!variant.polyphen_score) return null;
    const match = variant.polyphen_score.match(/^([a-z_]+)\(/i);
    return match ? match[1].replace(/_/g, " ") : null;
  };

  // Format AlphaMissense interpretation
  const getAlphaMissenseInterpretation = () => {
    if (!variant.alphamissense_class) return null;
    return variant.alphamissense_class.replace(/_/g, " ");
  };

  // Parse numeric values for summary
  const parseNumericValue = (value, parseFromString = false) => {
    if (!value) return null;
    if (parseFromString && typeof value === "string") {
      const match = value.match(/\(([0-9.-]+)\)/);
      if (match && match[1]) {
        return parseFloat(match[1]);
      }
    }
    return parseFloat(value) || null;
  };

  const summaryScores = {
    sift: parseNumericValue(variant.sift_score, true),
    polyphen: parseNumericValue(variant.polyphen_score, true),
    alphaMissense: parseNumericValue(variant.alphamissense_pathogenicity),
    revel: parseNumericValue(variant.revel_score),
    cadd: parseNumericValue(variant.cadd_phred),
    bayesDel: parseNumericValue(variant.bayesdel_addaf),
    metaRNN: parseNumericValue(variant.metarnn_score),
    fathmm: parseNumericValue(variant.fathmm_score),
    primateAI: parseNumericValue(variant.primateai_score),
  };

  return (
    <Box sx={cardStyle(cardColors.inSilico)}>
      <Box
        sx={{
          background: cardColors.inSilico.header,
          p: 1.5,
          borderTopLeftRadius: 6,
          borderTopRightRadius: 6,
        }}
      >
        <Typography
          color={cardColors.inSilico.headerText}
          variant="subtitle2"
          fontWeight="bold"
          align="center"
        >
          In Silico Predictions
        </Typography>
      </Box>

      <Box sx={{ p: 2 }}>
        {/* Prediction Summary Chart */}
        <PredictionSummary scores={summaryScores} />

        {/* Primary Prediction Scores in Grid */}
        <Typography
          variant="caption"
          color="#333333"
          fontWeight="bold"
          sx={{ mb: 1, display: "block", fontSize: "0.8rem" }}
        >
          Pathogenicity Predictions
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <PredictionSlider
              label="SIFT"
              value={variant.sift_score}
              maxValue={1}
              interpretation={getSiftInterpretation()}
              thresholds={[{ value: 0.05, label: "Deleterious" }]}
              invertColors={true}
              parseValue={true}
            />
          </Grid>

          <Grid item xs={6}>
            <PredictionSlider
              label="PolyPhen"
              value={variant.polyphen_score}
              maxValue={1}
              interpretation={getPolyphenInterpretation()}
              thresholds={[
                { value: 0.446, label: "Possibly" },
                { value: 0.908, label: "Probably" },
              ]}
              parseValue={true}
            />
          </Grid>

          <Grid item xs={6}>
            <PredictionSlider
              label="AlphaMissense"
              value={variant.alphamissense_pathogenicity}
              maxValue={1}
              interpretation={getAlphaMissenseInterpretation()}
              thresholds={[
                { value: 0.34, label: "Benign" },
                { value: 0.564, label: "Pathogenic" },
              ]}
            />
          </Grid>

          <Grid item xs={6}>
            <PredictionSlider
              label="REVEL"
              value={variant.revel_score}
              maxValue={1}
              interpretation={variant.revel_interpretation}
              thresholds={[
                { value: 0.5, label: "Pathogenic" },
                { value: 0.75, label: "Strong" },
              ]}
            />
          </Grid>

          <Grid item xs={6}>
            <PredictionSlider
              label="CADD (Phred)"
              value={variant.cadd_phred}
              maxValue={50}
              thresholds={[
                { value: 20, label: "Deleterious" },
                { value: 30, label: "High" },
              ]}
            />
          </Grid>

          <Grid item xs={6}>
            <PredictionSlider
              label="BayesDel (addAF)"
              value={variant.bayesdel_addaf}
              maxValue={1}
              thresholds={[
                { value: -0.36, label: "Benign" },
                { value: 0.28, label: "Pathogenic" },
              ]}
            />
          </Grid>

          <Grid item xs={6}>
            <PredictionSlider
              label="MetaRNN"
              value={variant.metarnn_score}
              maxValue={1}
              thresholds={[{ value: 0.5, label: "Pathogenic" }]}
            />
          </Grid>

          <Grid item xs={6}>
            <PredictionSlider
              label="PrimateAI"
              value={variant.primateai_score}
              maxValue={1}
              thresholds={[{ value: 0.8, label: "Pathogenic" }]}
            />
          </Grid>
        </Grid>

        {/* SpliceAI Section */}
        <SpliceAISection variant={variant} />
      </Box>
    </Box>
  );
}

export default InSilicoCard;
